import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  Chip,
  cn,
  Tooltip,
} from '@c2fo/liquidity';
import { InfoCircleIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import BuyerControlsDropdown from '@/enterprise/components/BuyerControlsDropdown';
import BuyerLogo from '@/enterprise/components/BuyerLogo';
import ClosedMarketChip from '@/enterprise/components/ClosedMarketChip';
import EmptyRender from '@/enterprise/components/EmptyRender';
import Skeleton from '@/enterprise/components/Skeleton';
import useClosedMarkets from '@/enterprise/data/useClosedMarkets';
import useGroupByBuyer, { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import { GlobalOfferWithContext } from '@/enterprise/features/globalOffers/GlobalOfferContext';
import { GlobalParticipationWithContext } from '@/enterprise/features/globalOffers/GlobalParticipationContext';
import MarketTable from '@/enterprise/features/homepage/marketTable/MarketTable';
import { OfferModalWithContext } from '@/enterprise/features/offerModal/OfferModalContext';
import { RecurringRulesFormWithContext } from '@/enterprise/features/recurringRules/RecurringRulesFormContext';
import NoAvailableMarkets from '@/enterprise/features/takerMarketTabs/NoAvailableMarkets';
import { useMarketStatsStatus } from '@/lib/useMarketStatsStatus';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import handleNaN from '@/utils/handleNaN';
import useLocaleFormat from '@/utils/useLocaleFormat';
import LendingInlineAd from '../features/lendingConnections/LendingInlineAd';
import ErrorLoadingMarkets from './ErrorLoadingMarkets';
import { IncludeEligibleInvoicesWithContext } from './IncludeEligibleInvoicesContext';
import NewDivisionAlert, { useEnableNewDivisionAlert } from './NewDivisionAlert';

// Buyer Card is above Lending Ad, so if only one buyer, Lending Ad shown on first index
// Otherwise, Lending Ad shown on second index
const getIndexForLendingAd = (buyers: BuyerGroupWithStats[]) => (buyers.length === 1 ? 0 : 1);

const BuyerCardsComponent = () => {
  const { t } = useTranslation();
  const { asDateStringText } = useLocaleFormat();
  const { selectedCurrency } = useCurrencyContext();
  const { data: takerMarkets } = useTakerMarkets();
  const enableNewDivisionAlert = useEnableNewDivisionAlert();
  const buyers = useGroupByBuyer({ takerMarkets, prioritizeNewDivisions: enableNewDivisionAlert });
  const { asCurrency, asNumber } = useLocaleFormat();
  const { listenToMarketStats } = useMarketStatsStatus();
  const { getClosedMarkets } = useClosedMarkets();

  if (buyers.length === 0) {
    return <NoAvailableMarkets />;
  }

  const lendingIndex = getIndexForLendingAd(buyers);

  return (
    <Accordion className="space-y-4" type="multiple">
      {buyers.map((buyer, index) => {
        const listeningForMarketStat = listenToMarketStats(
          buyer.takerMarkets.map(({ marketUuid, takerDivisionId, takerDivisionUuid, makerOrganizationUuid }) => ({
            marketUuid,
            takerId: takerDivisionId,
            takerDivisionUuid,
            makerOrganizationUuid,
          }))
        );

        const { hasClosedMarkets, closedMarketCount, closedInvoiceAmount } = getClosedMarkets(
          buyer.takerMarkets,
          'eligible'
        );
        const formattedClosedInvoiceAmount = asCurrency(closedInvoiceAmount, selectedCurrency);

        return (
          <div key={buyer.makerOrganizationUuid}>
            <AccordionItem
              key={buyer.makerOrganizationUuid}
              className="overflow-hidden rounded-2xl bg-white shadow"
              value={buyer.makerOrganizationUuid}
              {...(index === 0 && {
                'data-intercom-target': 'firstEligibleCustomer' satisfies IntercomDataTargetNames,
              })}
            >
              <AccordionHeader className="flex-col p-0">
                <>
                  <div className="flex w-full flex-col p-6 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-1 flex-col justify-between gap-4 md:flex-row md:items-center">
                      <div className="flex flex-1 flex-col justify-between gap-4 md:flex-row md:items-center md:pr-2">
                        {/* buyer info */}
                        <div className="relative flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4 truncate">
                            <BuyerLogo
                              buyerLogoUrl={buyer.makerOrganizationLogo}
                              buyerName={buyer.makerOrganizationName}
                              newDivisionCount={buyer.stats.newDivisionCount}
                              size="lg"
                            />
                            <div className="truncate">
                              <div className="truncate text-wrap">
                                <Link
                                  className="text-2xl font-bold text-blue-500"
                                  to={asEnterpriseLink(
                                    `customers/${buyer.makerOrganizationUuid}/markets/${buyer.takerMarkets[0].marketId}/division/${buyer.takerMarkets[0].takerDivisionId}/invoices/eligible`
                                  )}
                                >
                                  {buyer.makerOrganizationName}
                                </Link>
                              </div>
                              <div className="mt-1 flex flex-col flex-wrap items-start gap-1 text-wrap text-text-secondary md:flex-row md:items-center">
                                {buyer.stats.newDivisionCount > 0 && (
                                  <Chip
                                    className="hidden md:flex"
                                    isRound
                                    label={t('core.newWithCount', {
                                      formattedCount: asNumber({ value: buyer.stats.newDivisionCount }),
                                    })}
                                    size="xs"
                                    type="info"
                                    variant="filled"
                                  />
                                )}
                                <div>
                                  {`${t('core.divisionWithCount', {
                                    count: buyer.stats.totalDivisionCount,
                                    formattedCount: asNumber({ value: buyer.stats.totalDivisionCount }),
                                  })} | ${t('core.invoiceWithCount', {
                                    count: buyer.stats.eligibleInvoiceCount,
                                    formattedCount: asNumber({ value: buyer.stats.eligibleInvoiceCount }),
                                  })}`}
                                </div>
                                {hasClosedMarkets && (
                                  <ClosedMarketChip
                                    count={closedMarketCount}
                                    invoiceAmount={formattedClosedInvoiceAmount}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex md:hidden">
                            <BuyerControlsDropdown buyer={buyer} triggerVariant="ancillary" />
                          </div>
                        </div>
                        {/* buyer invoice info */}
                        <div className="md:text-right">
                          <div className="font-serif text-3xl font-bold">
                            {asCurrency(buyer.stats.totalInvoiceAmount, selectedCurrency)}
                          </div>
                          <div className="text-text-secondary">{t('core.totalAmount')}</div>
                        </div>
                      </div>
                      <div className="md:border-l-[0.5px] md:border-stroke md:pl-2">
                        {/* desktop trigger */}
                        <div className="hidden md:flex md:items-center">
                          <BuyerControlsDropdown buyer={buyer} triggerVariant="tertiary" />
                          <AccordionTrigger
                            {...(index === 0 && {
                              'data-intercom-target': 'expandButton' satisfies IntercomDataTargetNames,
                            })}
                          />
                        </div>
                      </div>
                    </div>
                    {/* mobile trigger */}
                    <div className="flex w-full justify-center md:hidden">
                      <AccordionTrigger className="h-11 w-full" variant="tertiary" />
                    </div>
                  </div>
                  <div className="w-full">
                    <NewDivisionAlert takerMarkets={buyer.takerMarkets} referrer="customers" className="px-6 pb-6" />
                  </div>
                  {/* buyer stats */}
                  <div className="hidden w-full bg-canvas px-10 py-4 text-text-secondary md:grid md:grid-cols-5 md:gap-6">
                    <div className="text-center">
                      <div className={cn('text-xl', { 'blur-sm': listeningForMarketStat })}>
                        {`${asNumber({ value: buyer.stats.divisionsParticipating })}/${asNumber({
                          value: buyer.stats.totalDivisionCount,
                        })}`}
                      </div>
                      <div className="text-sm font-medium">{t('core.divisionsParticipating')}</div>
                    </div>
                    <div className="text-center">
                      <div className={cn('text-xl', { 'blur-sm': listeningForMarketStat })}>
                        {asCurrency(buyer.stats.eligibleInvoiceAmount, selectedCurrency)}
                      </div>
                      <div className="text-sm font-medium">{t('core.includedAmount')}</div>
                    </div>
                    <div className="text-center">
                      <div className={cn('text-xl', { 'blur-sm': listeningForMarketStat })}>
                        {asCurrency(buyer.stats.estimatedEarlyPaymentAmount, selectedCurrency)}
                      </div>
                      <div className="text-sm font-medium">{t('core.estimatedEarlyPaymentAmount')}</div>
                    </div>
                    <div className="text-center">
                      <div className={cn('text-xl', { 'blur-sm': listeningForMarketStat })}>
                        {asNumber({
                          value: handleNaN(buyer.stats.avgDaysPaidEarly),
                          decimals: 0,
                        })}
                      </div>
                      <div className="text-sm font-medium">{t('core.averageDaysPaidEarly')}</div>
                    </div>
                    <div className="text-center">
                      <div className={cn('text-xl', { 'blur-sm': listeningForMarketStat })}>
                        <EmptyRender condition={!!buyer.stats.nextGoodFundsDateWithEligibleInvoices}>
                          {buyer.stats.nextGoodFundsDateWithEligibleInvoices &&
                            asDateStringText({ date: buyer.stats.nextGoodFundsDateWithEligibleInvoices })}
                        </EmptyRender>
                      </div>
                      <div className="flex items-center justify-center gap-1 text-sm font-medium">
                        <span>{t('core.nextEstimatedDepositDate')}</span>
                        <Tooltip>
                          <Tooltip.Trigger aria-label={t('core.nextEstimatedDepositDate')}>
                            <InfoCircleIcon className="fill-blue-500" />
                          </Tooltip.Trigger>
                          <Tooltip.Content
                            arrow
                            className="max-w-60 whitespace-pre-wrap text-center"
                            collisionPadding={24}
                            side="bottom"
                            variant="dark"
                          >
                            {t('core.nextEstimatedDepositDateTooltip')}
                          </Tooltip.Content>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </>
              </AccordionHeader>
              <AccordionContent className="overflow-y-auto p-0">
                <QueryBoundaries
                  LoadingComponent={() => (
                    <div className="space-y-4 p-4">
                      {Array.from({ length: 5 }).map((_, index) => (
                        <Skeleton key={index} height="h-24" />
                      ))}
                    </div>
                  )}
                >
                  <MarketTable takerMarkets={buyer.takerMarkets} referrer="customers" />
                </QueryBoundaries>
              </AccordionContent>
            </AccordionItem>
            {index === lendingIndex && <LendingInlineAd mode="buyers" />}
          </div>
        );
      })}
    </Accordion>
  );
};

const BuyerCards = () => (
  <QueryBoundaries
    LoadingComponent={() => (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} height="h-44" />
        ))}
      </div>
    )}
    ErrorComponent={() => <ErrorLoadingMarkets />}
  >
    <GlobalParticipationWithContext>
      <GlobalOfferWithContext>
        <OfferModalWithContext>
          <RecurringRulesFormWithContext>
            <IncludeEligibleInvoicesWithContext>
              <BuyerCardsComponent />
            </IncludeEligibleInvoicesWithContext>
          </RecurringRulesFormWithContext>
        </OfferModalWithContext>
      </GlobalOfferWithContext>
    </GlobalParticipationWithContext>
  </QueryBoundaries>
);

export default BuyerCards;
